import { motion } from 'framer-motion';
import { Hammer, TrendingUp, Sparkles, Users } from 'lucide-react';
import { PILLARS } from '@/data/content';

const ICONS = { build: Hammer, trade: TrendingUp, create: Sparkles, community: Users };

export default function EcosystemGrid() {
  return (
    <section
      id="ecosystem"
      data-testid="ecosystem-section"
      className="relative overflow-hidden py-24 sm:py-32"
      aria-label="The Toonverse ecosystem"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ background: 'radial-gradient(60% 50% at 50% 100%, rgba(22,119,255,0.12) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-[1400px] px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-electric">04 — ECOSYSTEM</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl lg:text-5xl">
            THE{' '}
            <span className="bg-gradient-to-r from-arc via-electric to-gold bg-clip-text text-transparent">
              TOONVERSE
            </span>
          </h2>
        </motion.div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => {
            const Icon = ICONS[p.id];
            return (
              <motion.article
                key={p.id}
                data-testid={`pillar-${p.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 0.6, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ y: -10 }}
                className="group relative flex h-full flex-col rounded-3xl border border-electric/15 bg-navy2/70 p-7 transition-colors duration-300"
                style={{ backdropFilter: 'blur(12px)' }}
              >
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{ boxShadow: `0 0 44px ${p.accent}44, inset 0 0 30px ${p.accent}18`, borderColor: p.accent }}
                />
                <div className="flex items-start justify-between">
                  <motion.span
                    className="flex h-14 w-14 items-center justify-center rounded-2xl border"
                    style={{ borderColor: `${p.accent}55`, backgroundColor: `${p.accent}14` }}
                    whileHover={{ rotate: -8, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  >
                    <Icon className="h-6 w-6" style={{ color: p.accent }} aria-hidden="true" />
                  </motion.span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-snow/30">{p.index}</span>
                </div>
                <h3
                  className="mt-8 font-display text-xl font-extrabold uppercase tracking-tight"
                  style={{ color: p.accent }}
                  data-testid={`pillar-title-${p.id}`}
                >
                  {p.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-snow/65" data-testid={`pillar-desc-${p.id}`}>
                  {p.desc}
                </p>
                <span
                  aria-hidden="true"
                  className="mt-6 block h-px w-full origin-left scale-x-0 bg-gradient-to-r transition-transform duration-500 group-hover:scale-x-100"
                  style={{ background: `linear-gradient(90deg, ${p.accent}, transparent)` }}
                />
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
